export interface Project {
  id: string;
  name: string;
  directory: string;
  status: PROJECT_STATUS_LIST;
  uploadTime: Date;
  useArMarker: boolean;
  jobSiteId?: string;
  timezone: number;
  userId: string;
  useViDoc: boolean;
  las_file?: string;
  ifc_file?: string;
  vertical?: string;
  horizontal?: string;
  ar_color?: string;
}

export interface PhotoURL {
  id: number;
  name: string;
  url: string;
}

export interface ProjectList {
  projects: Project[];
}

export interface ProjectListLimit {
  total: number; // ログインユーザーが所属している現場のデータ全て
  hitsTotal?: number; // 入力した検索条件にヒットするプロジェクト件数
  projects: Project[];
}

export interface ProjectUserList {
  userId: string;
  name: string;
}

export interface ProjectDetail {
  id: string;
  name: string;
  jobsiteName: string;
  jobsiteId: string;
  photographer: string;
  size: string;
  status: PROJECT_STATUS_LIST;
  uploadTime: Date;
  useViDoc: boolean;
  imageResolution: string;
  numberOfPoints: number;
  pix4dErrorMessage: string;
  pix4dErrorCode: number;
  photoURLs: PhotoURL[];
  metadata: MetaData;
  sfmProcessId: string;
  helmertProcessId: string;
  las_file?: string;
  ifc_file?: string;
  pgpConsume?: number;
}

export interface MetaData {
  trigger_type: string;
  overlap_threshold: number;
  angle_threshold_degrees: number;
  distance_threshold_meters: number;
  camera_offset_support_type: string | null;
  mobile_device_description: string | null;
  app_version: string;
  os: string;
  os_version: string;
}

export const getMetaData = (metaData: any): MetaData => {
  return {
    trigger_type: metaData.trigger_type,
    overlap_threshold: metaData.overlap_threshold,
    angle_threshold_degrees: metaData.angle_threshold_degrees,
    distance_threshold_meters: metaData.distance_threshold_meters,
    camera_offset_support_type: metaData.camera_offset_support_type,
    mobile_device_description: metaData.mobile_device_description,
    app_version: metaData.app_version,
    os: metaData.os,
    os_version: metaData.os_version,
  };
};

export interface ProjectViDoc {
  id: string;
  name: string;
  horizontal: string;
  vertical: string;
  rotation: string;
  originEasting: string;
  originNorthing: string;
  originLatitude: string;
  originLongitude: string;
  scaleFactor: string;
  verticalOffset: string;
  useViDoc: string;
  inclineX: string;
  inclineY: string;
  conditionFlag: boolean;
  conversionLock: boolean;
}

export interface DetailInfo {
  date: string;
  site: string;
  dataSize: string;
}

export interface Photo {
  name: string;
  data?: string;
}

export interface userSetting {
  userId: string;
  projectionDefault: boolean;
  projectionPoint: boolean;
  coordinateSetting: boolean;
  colorTheme: string;
  linkDashboard: boolean;
  pointCloudBgcolor: string;
  pointCloudBgalpha: number;
}

export interface Photos {
  total: number;
  photos: Photo[];
}

export const POINT_CLOUD_TYPE = {
  SIMPLE: "simple",
  SFM: "sfm",
  HELMERT: "helmert",
  UNKNOWN: "unknown",
} as const;

export const getPointCloudType = (str: string): POINT_CLOUD_TYPE => {
  switch (str) {
    case POINT_CLOUD_TYPE.SIMPLE:
      return POINT_CLOUD_TYPE.SIMPLE;
    case POINT_CLOUD_TYPE.SFM:
      return POINT_CLOUD_TYPE.SFM;
    case POINT_CLOUD_TYPE.HELMERT:
      return POINT_CLOUD_TYPE.HELMERT;
    default:
      return POINT_CLOUD_TYPE.UNKNOWN;
  }
};

export type POINT_CLOUD_TYPE =
  (typeof POINT_CLOUD_TYPE)[keyof typeof POINT_CLOUD_TYPE];

export type ProjectDownloadPointCloudInput = {
  simple: boolean;
  sfm: boolean;
  helmert: boolean;
};

export type ProjectDownloadAllInput = {
  LiDAR?: boolean;
  sfm?: boolean;
  helmert?: boolean;
  referencePoints?: boolean;
  verificationPoints?: boolean;
  images?: boolean;
  qualityReport?: boolean;
  snapshot?: string | null;
  fileType?: string;
};

export enum PROJECT_STATUS_ENUM {
  INCOMPLETE = 0, // 未完了
  PROCESSING = 1, // 処理中
  COMPLETED = 2, // 完了
  WAITING = 3, // 待機中
  ERROR = -1, // エラー
}

export type PROJECT_STATUS_LIST = {
  lidar_upload: PROJECT_STATUS_ENUM;
  sfm_processing: PROJECT_STATUS_ENUM;
  helmert_transformation: PROJECT_STATUS_ENUM;
  verification_sfm: PROJECT_STATUS_ENUM;
  verification_helmert: PROJECT_STATUS_ENUM;
  dashboard_upload_las_sfm: PROJECT_STATUS_ENUM;
  dashboard_upload_las_helmert: PROJECT_STATUS_ENUM;
  dashboard_upload_txt_sfm: PROJECT_STATUS_ENUM;
  dashboard_upload_txt_helmert: PROJECT_STATUS_ENUM;
  landlog_viewer_upload_sfm: PROJECT_STATUS_ENUM;
  landlog_viewer_upload_helmert: PROJECT_STATUS_ENUM;
  coordinate_transformation: PROJECT_STATUS_ENUM;
  point_splitting_lidar: PROJECT_STATUS_ENUM;
  point_splitting_sfm: PROJECT_STATUS_ENUM;
  point_splitting_helmert: PROJECT_STATUS_ENUM;
  remove_lidar: PROJECT_STATUS_ENUM;
  remove_sfm: PROJECT_STATUS_ENUM;
  remove_helmert: PROJECT_STATUS_ENUM;
  point_txt_lidar: PROJECT_STATUS_ENUM;
  point_txt_sfm: PROJECT_STATUS_ENUM;
  point_txt_helmert: PROJECT_STATUS_ENUM;
  ply_created: PROJECT_STATUS_ENUM;
  ply_upload: PROJECT_STATUS_ENUM;
  is_ar: boolean;
  ar_processing: PROJECT_STATUS_ENUM;
};

export const getStatusList = (projectRecord: any): PROJECT_STATUS_LIST => {
  return {
    lidar_upload: projectRecord.lidar_upload,
    sfm_processing: projectRecord.sfm_processing,
    helmert_transformation: projectRecord.helmert_transformation,
    verification_sfm: projectRecord.verification_sfm,
    verification_helmert: projectRecord.verification_helmert,
    dashboard_upload_las_sfm: projectRecord.dashboard_upload_las_sfm,
    dashboard_upload_las_helmert: projectRecord.dashboard_upload_las_helmert,
    dashboard_upload_txt_sfm: projectRecord.dashboard_upload_txt_sfm,
    dashboard_upload_txt_helmert: projectRecord.dashboard_upload_txt_helmert,
    landlog_viewer_upload_sfm: projectRecord.landlog_viewer_upload_sfm,
    landlog_viewer_upload_helmert: projectRecord.landlog_viewer_upload_helmert,
    coordinate_transformation: projectRecord.coordinate_transformation,
    point_splitting_lidar: projectRecord.point_splitting_lidar,
    point_splitting_sfm: projectRecord.point_splitting_sfm,
    point_splitting_helmert: projectRecord.point_splitting_helmert,
    remove_lidar: projectRecord.remove_lidar,
    remove_sfm: projectRecord.remove_sfm,
    remove_helmert: projectRecord.remove_helmert,
    point_txt_lidar: projectRecord.point_txt_lidar,
    point_txt_sfm: projectRecord.point_txt_sfm,
    point_txt_helmert: projectRecord.point_txt_helmert,
    ply_created: projectRecord.ply_created,
    ply_upload: projectRecord.ply_upload,
    is_ar: projectRecord.is_ar,
    ar_processing: projectRecord.ar_processing,
  };
};

export type ProcessingStatus = {
  coordinate_transformation: boolean;
  helmert_transformation: boolean;
  point_splitting: boolean;
  remove: boolean;
  txt_conversion: boolean;
};

export enum ECoordinateOptions {
  DASHBOARD = "DASHBOARD",
  PUBLIC_COORDINATE = "PUBLIC_COORDINATE",
  LOCALIZATION = "LOCALIZATION",
  NONE = "NONE",
}

export type RtkFixData = {
  gps: {
    altitude: number;
    latitude: number;
    longitude: number;
    xyAccuracy: number;
    zAccuracy: number;
  };
  imageName: string;
  rotation: {
    pitch: number;
    roll: number;
    yaw: number;
  };
  time: number;
  transformMatrix: number[];
  accuracy: RTK_FIX_ACCURACY;
};

export enum RTK_FIX_ACCURACY {
  GPS = "GPS",
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
}
